import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2, X } from 'lucide-react';
import { supabase } from '../../../shared/lib/supabase';

const UPSELL_TIERS = [
  {
    id: 'pro' as const,
    name: 'Pro',
    price: 49,
    description: 'Best for active job seekers',
    features: [
      'Job Match AI analysis (20/month)',
      'Unlimited AI coaching sessions',
      'Interview Strategy Coach',
      'Career Development Strategist',
      'Priority support',
    ],
  },
  {
    id: 'premium' as const,
    name: 'Premium',
    price: 79,
    description: 'Complete career intelligence',
    features: [
      'Unlimited Job Match AI',
      'All AI coaching personas',
      'Voice-enabled coaching',
      'Executive Interview Coach',
      'Priority phone support',
      'Early access to features',
    ],
  },
];

export function SubscriptionUpsell() {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [selectedTier, setSelectedTier] = useState<'pro' | 'premium' | null>('pro');
  const [dismissed, setDismissed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async () => {
    if (!selectedTier) return;

    setProcessing(true);
    setError(null);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Please log in to continue');
      }

      const origin = window.location.origin;
      const successUrl = `${origin}/dashboard?subscription=success`;
      const cancelUrl = `${origin}/thank-you`;

      const apiUrl = `${
        import.meta.env.VITE_SUPABASE_URL
      }/functions/v1/stripe-create-upsell-checkout`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          tier: selectedTier,
          successUrl,
          cancelUrl,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create subscription checkout');
      }

      const { url } = await response.json();

      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err: any) {
      console.error('Subscription error:', err);
      setError(err.message || 'Failed to start subscription. Please try again.');
      setProcessing(false);
    }
  };

  const handleDismiss = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        await supabase
          .from('profiles')
          .update({ post_purchase_upsell_dismissed_at: new Date().toISOString() })
          .eq('id', user.id);
      }

      setDismissed(true);
    } catch (err) {
      console.error('Error dismissing upsell:', err);
      setDismissed(true);
    }
  };

  if (dismissed) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 border border-purple-500/30 rounded-lg p-8 mb-8 relative">
      <button
        onClick={handleDismiss}
        className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        aria-label="Dismiss"
      >
        <X size={24} />
      </button>

      <div className="flex items-center mb-4">
        <Sparkles className="text-purple-400 mr-3" size={32} />
        <h2 className="text-2xl font-bold text-white">Unlock Ongoing Career Insights</h2>
      </div>

      <p className="text-gray-300 mb-6">
        Your report is just the beginning. Add a monthly subscription to get continuous career
        guidance, unlimited AI coaching, and advanced job matching tools.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {UPSELL_TIERS.map((tier) => (
          <button
            key={tier.id}
            onClick={() => setSelectedTier(tier.id)}
            className={`p-6 rounded-lg border-2 text-left transition-all ${
              selectedTier === tier.id
                ? 'border-purple-500 bg-purple-900/20 shadow-lg'
                : 'border-slate-600 bg-slate-800/30 hover:border-slate-500'
            }`}
          >
            <div className="flex items-baseline mb-3">
              <span className="text-2xl font-bold text-white">${tier.price}</span>
              <span className="text-gray-400 text-sm ml-1">/month</span>
            </div>

            <h3 className="text-xl font-bold text-white mb-2">{tier.name}</h3>
            <p className="text-sm text-gray-400 mb-4">{tier.description}</p>

            <ul className="space-y-2">
              {tier.features.map((feature, i) => (
                <li key={i} className="text-sm text-gray-300 flex items-start">
                  <span className="text-purple-400 mr-2">•</span>
                  {feature}
                </li>
              ))}
            </ul>

            {selectedTier === tier.id && (
              <div className="mt-4 pt-4 border-t border-purple-500/30">
                <span className="text-sm font-medium text-purple-400">Selected</span>
              </div>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="flex gap-4">
        <button
          onClick={handleSubscribe}
          disabled={processing || !selectedTier}
          className={`flex-1 py-3 px-6 rounded-lg font-bold text-white transition-all flex items-center justify-center ${
            processing || !selectedTier
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-lg'
          }`}
        >
          {processing ? (
            <>
              <Loader2 className="animate-spin mr-2" size={20} />
              Processing...
            </>
          ) : (
            `Subscribe for $${selectedTier ? UPSELL_TIERS.find((t) => t.id === selectedTier)?.price : 0}/month`
          )}
        </button>

        <button
          onClick={handleDismiss}
          className="px-6 py-3 text-gray-300 hover:text-white transition-colors font-medium"
        >
          Maybe later
        </button>
      </div>

      <div className="mt-4 text-center text-sm text-gray-400">
        Cancel anytime, no penalties. 30-day satisfaction guarantee.
      </div>
    </div>
  );
}
